import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Plus, AlertCircle } from 'lucide-react';
import { useSetAtom } from 'jotai';
import {
  createSourceActionAtom,
  updateSourceActionAtom,
} from '../stores/sourcesStore';
import { toast } from 'sonner';
import type { SourceType } from '../types/api';
import { SourceFormData, SourceResponse } from '@/types/sources';

interface AddSourceDialogProps {
  trigger?: React.ReactNode;
  source?: SourceResponse; // If provided, dialog is in edit mode
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AddSourceDialog({
  trigger,
  source,
  open: controlledOpen,
  onOpenChange,
}: AddSourceDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createSource = useSetAtom(createSourceActionAtom);
  const updateSource = useSetAtom(updateSourceActionAtom);

  const isEditMode = !!source;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SourceFormData>({
    defaultValues: source
      ? {
          firstName: source.firstName || '',
          lastName: source.lastName || '',
          email: source.email || '',
          phone: source.phone || '',
          title: source.title || '',
          company: source.company || '',
          industry: source.industry || '',
          sourceType: source.sourceType || 'other',
          linkedinUrl: source.linkedinUrl || '',
          twitterHandle: source.twitterHandle || '',
          website: source.website || '',
          expertiseAreas: source.expertiseAreas || '',
          tags: source.tags || '',
          notes: source.notes || '',
          preferredContactMethod: source.preferredContactMethod || 'email',
          consentGiven: source.consentGiven || false,
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          title: '',
          company: '',
          industry: '',
          sourceType: 'other',
          linkedinUrl: '',
          twitterHandle: '',
          website: '',
          expertiseAreas: '',
          tags: '',
          notes: '',
          preferredContactMethod: 'email',
          consentGiven: false,
        },
  });

  // Reset form when source changes (for edit mode)
  useEffect(() => {
    if (source) {
      reset({
        firstName: source.firstName || '',
        lastName: source.lastName || '',
        email: source.email || '',
        phone: source.phone || '',
        title: source.title || '',
        company: source.company || '',
        industry: source.industry || '',
        sourceType: source.sourceType || 'other',
        linkedinUrl: source.linkedinUrl || '',
        twitterHandle: source.twitterHandle || '',
        website: source.website || '',
        expertiseAreas: source.expertiseAreas || '',
        tags: source.tags || '',
        notes: source.notes || '',
        preferredContactMethod: source.preferredContactMethod || 'email',
        consentGiven: source.consentGiven || false,
      });
    }
  }, [source, reset]);

  const sourceType = watch('sourceType');

  const onSubmit = async (data: SourceFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (isEditMode && source) {
        await updateSource(source.id, data);
        toast.success('Source updated successfully');
      } else {
        await createSource(data);
        toast.success('Source added successfully');
      }

      setOpen(false);
      reset();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : isEditMode
            ? 'Failed to update source'
            : 'Failed to add source';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEditMode && (
        <DialogTrigger asChild>
          {trigger || (
            <Button size='sm' className='gap-2'>
              <Plus className='h-4 w-4' />
              Add Source
            </Button>
          )}
        </DialogTrigger>
      )}

      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Source' : 'Add New Source'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the source information in your contact database.'
              : 'Add a new source to your contact database for journalism purposes.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant='destructive'>
            <AlertCircle className='h-4 w-4' />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          {/* Basic Information */}
          <div className='space-y-4'>
            <h4>Basic Information</h4>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='first_name'>
                  First Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='first_name'
                  {...register('firstName', {
                    required: 'First name is required',
                    minLength: {
                      value: 2,
                      message: 'First name must be at least 2 characters',
                    },
                  })}
                  placeholder='Enter first name'
                />
                {errors.firstName && (
                  <p className='text-sm text-destructive'>
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='last_name'>
                  Last Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='last_name'
                  {...register('lastName', {
                    required: 'Last name is required',
                    minLength: {
                      value: 2,
                      message: 'Last name must be at least 2 characters',
                    },
                  })}
                  placeholder='Enter last name'
                />
                {errors.lastName && (
                  <p className='text-sm text-destructive'>
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='email'>Email</Label>
                <Input
                  id='email'
                  type='email'
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  placeholder='Enter email address'
                />
                {errors.email && (
                  <p className='text-sm text-destructive'>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='phone'>Phone</Label>
                <Input
                  id='phone'
                  {...register('phone')}
                  placeholder='Enter phone number'
                />
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className='space-y-4'>
            <h4>Professional Information</h4>

            <div className='space-y-2'>
              <Label htmlFor='title'>Job Title</Label>
              <Input
                id='title'
                {...register('title')}
                placeholder='e.g. VP of Product, Co-founder, Investment Director'
              />
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='company'>Company</Label>
                <Input
                  id='company'
                  {...register('company')}
                  placeholder='Enter company name'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='industry'>Industry</Label>
                <Input
                  id='industry'
                  {...register('industry')}
                  placeholder='e.g. Fintech, Technology, Venture Capital'
                />
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='source_type'>Source Type</Label>
              <Select
                value={sourceType}
                onValueChange={(value: SourceType) =>
                  setValue('sourceType', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='executive'>Executive</SelectItem>
                  <SelectItem value='founder'>Founder</SelectItem>
                  <SelectItem value='investor'>Investor</SelectItem>
                  <SelectItem value='journalist'>Journalist</SelectItem>
                  <SelectItem value='analyst'>Analyst</SelectItem>
                  <SelectItem value='spokesperson'>Spokesperson</SelectItem>
                  <SelectItem value='expert'>Expert</SelectItem>
                  <SelectItem value='other'>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Social Media & Web */}
          <div className='space-y-4'>
            <h4>Online Presence</h4>

            <div className='space-y-2'>
              <Label htmlFor='linkedin_url'>LinkedIn URL</Label>
              <Input
                id='linkedin_url'
                {...register('linkedinUrl', {
                  pattern: {
                    value:
                      /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/,
                    message: 'Please enter a valid LinkedIn profile URL',
                  },
                })}
                placeholder='https://linkedin.com/in/username'
              />
              {errors.linkedinUrl && (
                <p className='text-sm text-destructive'>
                  {errors.linkedinUrl.message}
                </p>
              )}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='twitter_handle'>Twitter Handle</Label>
                <Input
                  id='twitter_handle'
                  {...register('twitterHandle', {
                    pattern: {
                      value: /^@?[a-zA-Z0-9_]{1,15}$/,
                      message: 'Please enter a valid Twitter handle',
                    },
                  })}
                  placeholder='@username'
                />
                {errors.twitterHandle && (
                  <p className='text-sm text-destructive'>
                    {errors.twitterHandle.message}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='website'>Website</Label>
                <Input
                  id='website'
                  {...register('website', {
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message:
                        'Please enter a valid URL starting with http:// or https://',
                    },
                  })}
                  placeholder='https://example.com'
                />
                {errors.website && (
                  <p className='text-sm text-destructive'>
                    {errors.website.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Expertise and Tags */}
          <div className='space-y-4'>
            <h4>Expertise & Classification</h4>

            <div className='space-y-2'>
              <Label htmlFor='expertise_areas'>Expertise Areas</Label>
              <Input
                id='expertise_areas'
                {...register('expertiseAreas')}
                placeholder='Separate with commas, e.g. Fintech, Product Strategy, European Markets'
              />
              <p className='text-xs text-muted-foreground'>
                List the subject areas this source can provide insights on
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='tags'>Tags</Label>
              <Input
                id='tags'
                {...register('tags')}
                placeholder='Separate with commas, e.g. fintech, klarna, payments, europe'
              />
              <p className='text-xs text-muted-foreground'>
                Tags for categorization and search
              </p>
            </div>
          </div>

          {/* Contact Preferences */}
          <div className='space-y-4'>
            <h4>Contact Preferences</h4>

            <div className='space-y-2'>
              <Label htmlFor='preferred_contact_method'>
                Preferred Contact Method
              </Label>
              <Select
                value={watch('preferredContactMethod') || ''}
                onValueChange={(value: string) =>
                  setValue('preferredContactMethod', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='email'>Email</SelectItem>
                  <SelectItem value='phone'>Phone</SelectItem>
                  <SelectItem value='linkedin'>LinkedIn</SelectItem>
                  <SelectItem value='other'>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-center space-x-2'>
              <Checkbox
                id='consent_given'
                checked={watch('consentGiven')}
                onCheckedChange={checked =>
                  setValue('consentGiven', checked as boolean)
                }
              />
              <Label htmlFor='consent_given' className='text-sm'>
                Contact consent given for journalistic purposes
              </Label>
            </div>
          </div>

          {/* Notes */}
          <div className='space-y-2'>
            <Label htmlFor='notes'>Notes</Label>
            <Textarea
              id='notes'
              {...register('notes')}
              placeholder='Add any relevant notes about this source...'
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className='flex justify-end gap-2 pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting} className='gap-2'>
              {isSubmitting && (
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
              )}
              {isEditMode ? 'Update Source' : 'Add Source'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
